import * as React from 'react';
import { Check, X } from 'lucide-react';

import { cn } from '../lib/cn';
import { dedupeById } from '../lib/dedupeById';
import { Button } from './Button';
import { Input } from './Input';
import { Popover, PopoverAnchor, PopoverContent } from './popover';

export interface AutocompleteMultiSelectOption {
  id: string;
  label: string;
  disabled?: boolean | undefined;
}

export interface AutocompleteMultiSelectProps {
  label: string;
  options: AutocompleteMultiSelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  onSearchTermChange?: ((term: string) => void) | undefined;
  searchDebounceMs?: number | undefined;
  loading?: boolean | undefined;
  placeholder?: string | undefined;
  emptyMessage?: string | undefined;
  minSearchLength?: number | undefined;
  maxResults?: number | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
  controlClassName?: string | undefined;
  showSelectedValues?: boolean | undefined;
  inlineSelectedValues?: boolean | undefined;
}

export function AutocompleteMultiSelect({
  label,
  options,
  value,
  onValueChange,
  onSearchTermChange,
  searchDebounceMs = 0,
  loading = false,
  placeholder = 'Search options',
  emptyMessage = 'No options found.',
  minSearchLength = 0,
  maxResults = 50,
  disabled = false,
  className,
  controlClassName,
  showSelectedValues = true,
  inlineSelectedValues = false,
}: AutocompleteMultiSelectProps) {
  const inputId = React.useId();
  const listboxId = `${inputId}-listbox`;
  const controlRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const pointerSelectedOptionRef = React.useRef<string | null>(null);
  const searchChangedRef = React.useRef(false);
  const onSearchTermChangeRef = React.useRef(onSearchTermChange);
  const [open, setOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [localResultsDismissed, setLocalResultsDismissed] = React.useState(false);
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  const [query, setQuery] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [waitingForServerSearch, setWaitingForServerSearch] = React.useState(false);
  const normalizedQuery = query.trim().toLowerCase();
  const resolvedOptions = React.useMemo(() => dedupeById(options), [options]);
  const resolvingSelectedValues =
    loading && value.some((id) => !resolvedOptions.some((option) => option.id === id));
  const selectedOptions = resolveSelectedOptions(resolvedOptions, value, resolvingSelectedValues);
  const selectedIds = new Set(value);
  const isServerSearch = Boolean(onSearchTermChange);
  const shouldShowResults = isServerSearch ? normalizedQuery.length >= minSearchLength : true;
  const effectiveLoading = loading || waitingForServerSearch;
  const effectiveDisabled = disabled || resolvingSelectedValues;
  const inputPlaceholder = resolveInputPlaceholder(
    resolvingSelectedValues,
    selectedOptions.length,
    placeholder,
  );
  const filteredOptions = React.useMemo(() => {
    if (!shouldShowResults) {
      return [];
    }

    if (isServerSearch) {
      return resolvedOptions.slice(0, maxResults);
    }

    return resolvedOptions
      .filter((option) => option.label.toLowerCase().includes(normalizedQuery))
      .slice(0, maxResults);
  }, [isServerSearch, maxResults, normalizedQuery, resolvedOptions, shouldShowResults]);
  const localResultsAvailable =
    !isServerSearch && focused && !localResultsDismissed && filteredOptions.length > 0;
  const effectiveOpen = (open || localResultsAvailable) && !effectiveDisabled;

  React.useEffect(() => {
    onSearchTermChangeRef.current = onSearchTermChange;
  }, [onSearchTermChange]);

  React.useEffect(() => {
    if (!onSearchTermChangeRef.current || !searchChangedRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      searchChangedRef.current = false;
      const term = query.trim();
      onSearchTermChangeRef.current?.(term.length >= minSearchLength ? term : '');
      setWaitingForServerSearch(false);
    }, searchDebounceMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [minSearchLength, query, searchDebounceMs]);

  function preparePopover() {
    setPortalContainer(controlRef.current?.closest('[role="dialog"]') ?? null);
  }

  function closeAndDismissLocalResults() {
    setOpen(false);
    setLocalResultsDismissed(true);
    setHighlightedIndex(-1);
  }

  function removeOption(optionId: string) {
    onValueChange(value.filter((id) => id !== optionId));
    closeAndDismissLocalResults();
  }

  function selectOption(option: AutocompleteMultiSelectOption) {
    if (!option.disabled) {
      onValueChange(
        value.includes(option.id) ? value.filter((id) => id !== option.id) : [...value, option.id],
      );
      setHighlightedIndex(-1);
      window.requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }

  function handleOptionPointerDown(
    event: React.PointerEvent<HTMLButtonElement>,
    option: AutocompleteMultiSelectOption,
  ) {
    event.preventDefault();
    pointerSelectedOptionRef.current = option.id;
    selectOption(option);
  }

  function handleOptionClick(option: AutocompleteMultiSelectOption) {
    if (pointerSelectedOptionRef.current === option.id) {
      pointerSelectedOptionRef.current = null;
      return;
    }

    selectOption(option);
  }

  function handleOptionKeyDown(
    event: React.KeyboardEvent<HTMLButtonElement>,
    option: AutocompleteMultiSelectOption,
  ) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectOption(option);
    }
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' && effectiveOpen && filteredOptions.length) {
      event.preventDefault();
      setHighlightedIndex((current) => (current < filteredOptions.length - 1 ? current + 1 : 0));
    }
    if (event.key === 'ArrowUp' && effectiveOpen && highlightedIndex >= 0) {
      event.preventDefault();
      setHighlightedIndex((current) => (current > 0 ? current - 1 : -1));
    }
    if (event.key === 'Enter' && effectiveOpen && highlightedIndex >= 0) {
      event.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option) selectOption(option);
    }
    if (event.key === 'Escape') {
      closeAndDismissLocalResults();
    }
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const nextQuery = event.target.value;
    const normalizedNextQuery = nextQuery.trim().toLowerCase();
    const serverQueryIsLongEnough = normalizedNextQuery.length >= minSearchLength;
    const hasLocalMatch = resolvedOptions.some((option) =>
      option.label.toLowerCase().includes(normalizedNextQuery),
    );

    searchChangedRef.current = Boolean(onSearchTermChange);
    setQuery(nextQuery);
    setLocalResultsDismissed(false);
    setHighlightedIndex(-1);
    setWaitingForServerSearch(Boolean(onSearchTermChange) && serverQueryIsLongEnough);
    preparePopover();
    setOpen(isServerSearch ? serverQueryIsLongEnough : hasLocalMatch);
  }

  const selectedValueChips =
    showSelectedValues && selectedOptions.length ? (
      <div
        className={cn(
          'flex max-w-full min-w-0 gap-2',
          inlineSelectedValues ? 'shrink-0 flex-nowrap gap-1' : 'mt-2 flex-wrap overflow-hidden',
        )}
      >
        {selectedOptions.map((option) => (
          <span
            key={option.id}
            className={cn(
              'border-border bg-muted text-foreground inline-flex max-w-full min-w-0 items-center gap-1 rounded-md border px-2 text-sm',
              inlineSelectedValues ? 'shrink-0 py-0.5' : 'py-1',
            )}
          >
            <span className="min-w-0 flex-1 truncate">{option.label}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5 shrink-0 rounded-full"
              aria-label={`Remove ${option.label}`}
              disabled={effectiveDisabled || option.disabled}
              onClick={() => {
                removeOption(option.id);
              }}
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </Button>
          </span>
        ))}
      </div>
    ) : null;

  return (
    <Popover
      open={effectiveOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) closeAndDismissLocalResults();
      }}
    >
      <div className={cn('grid min-w-0 gap-2', className)}>
        <label htmlFor={inputId} className="sr-only">
          {label}
        </label>
        <PopoverAnchor asChild>
          <div
            ref={controlRef}
            className={cn(
              'border-input focus-within:ring-ring min-h-9 w-full max-w-full min-w-0 rounded-md border bg-transparent py-1 shadow-[var(--shadow-control)] transition-colors focus-within:ring-1',
              inlineSelectedValues && showSelectedValues && selectedOptions.length > 0
                ? 'px-1'
                : 'px-3',
              inlineSelectedValues
                ? 'flex flex-nowrap items-center gap-1 overflow-x-auto'
                : 'overflow-hidden',
              effectiveDisabled && 'cursor-not-allowed opacity-50',
              controlClassName,
            )}
          >
            {inlineSelectedValues ? selectedValueChips : null}
            <Input
              ref={inputRef}
              id={inputId}
              value={query}
              disabled={effectiveDisabled}
              aria-busy={resolvingSelectedValues || undefined}
              aria-label={label}
              aria-expanded={effectiveOpen}
              aria-autocomplete="list"
              aria-controls={effectiveOpen ? listboxId : undefined}
              aria-activedescendant={
                effectiveOpen && highlightedIndex >= 0
                  ? `${listboxId}-option-${String(highlightedIndex)}`
                  : undefined
              }
              placeholder={inputPlaceholder}
              className={cn(
                'h-6 border-0 px-0 py-0 shadow-none focus-visible:ring-0',
                inlineSelectedValues && 'min-w-20 flex-1',
              )}
              onFocus={() => {
                setFocused(true);
                setLocalResultsDismissed(false);
                preparePopover();
                if (!isServerSearch && resolvedOptions.length) {
                  setOpen(true);
                  setHighlightedIndex(-1);
                }
                if (
                  isServerSearch &&
                  normalizedQuery.length >= minSearchLength &&
                  !resolvingSelectedValues
                ) {
                  setOpen(true);
                  setHighlightedIndex(-1);
                }
              }}
              onBlur={() => {
                setFocused(false);
              }}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
            />
            {!inlineSelectedValues ? selectedValueChips : null}
          </div>
        </PopoverAnchor>
      </div>
      <PopoverContent
        align="start"
        side="bottom"
        avoidCollisions
        collisionPadding={8}
        portalContainer={portalContainer}
        className="z-[60] w-[var(--radix-popover-trigger-width)] p-0"
        onInteractOutside={(event) => {
          const target = event.target;

          if (target instanceof Node && controlRef.current?.contains(target)) {
            event.preventDefault();
          }
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        <div
          id={listboxId}
          className="max-h-64 overflow-x-hidden overflow-y-auto p-1"
          role="listbox"
          aria-multiselectable="true"
        >
          {shouldShowResults && effectiveLoading ? (
            <AutocompleteEmptyMessage>Searching…</AutocompleteEmptyMessage>
          ) : null}
          {shouldShowResults && !effectiveLoading && !filteredOptions.length ? (
            <AutocompleteEmptyMessage>{emptyMessage}</AutocompleteEmptyMessage>
          ) : null}
          {!effectiveLoading
            ? filteredOptions.map((option, index) => {
                const selected = selectedIds.has(option.id);
                const optionDisabled = option.disabled === true;

                return (
                  <button
                    key={option.id}
                    id={`${listboxId}-option-${String(index)}`}
                    type="button"
                    role="option"
                    aria-disabled={optionDisabled}
                    aria-selected={selected}
                    disabled={optionDisabled}
                    className={cn(
                      'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none select-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
                      highlightedIndex === index && 'bg-accent text-accent-foreground',
                    )}
                    data-selected={selected ? 'true' : 'false'}
                    onPointerDown={(event) => {
                      handleOptionPointerDown(event, option);
                    }}
                    onPointerMove={() => {
                      setHighlightedIndex(index);
                    }}
                    onClick={() => {
                      handleOptionClick(option);
                    }}
                    onKeyDown={(event) => {
                      handleOptionKeyDown(event, option);
                    }}
                  >
                    <span
                      className={cn(
                        'border-input flex h-4 w-4 items-center justify-center rounded-sm border',
                        selected && 'bg-primary text-primary-foreground border-primary',
                      )}
                      aria-hidden="true"
                    >
                      {selected ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  </button>
                );
              })
            : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AutocompleteEmptyMessage({ children }: { children: React.ReactNode }) {
  return <div className="py-6 text-center text-sm">{children}</div>;
}

function resolveInputPlaceholder(
  resolvingSelectedValues: boolean,
  selectedCount: number,
  placeholder: string,
) {
  if (resolvingSelectedValues) return 'Loading…';
  if (selectedCount) return '';
  return placeholder;
}

function resolveSelectedOptions(
  options: AutocompleteMultiSelectOption[],
  value: string[],
  resolving: boolean,
) {
  const byId = new Map(options.map((option) => [option.id, option]));
  return value.flatMap((id) => {
    const option = byId.get(id);
    if (option) return [option];
    return resolving ? [] : [{ id, label: id }];
  });
}
